import {
  MAX_SUPPORT_ISSUANCE_CREDITS_PER_DAY,
  type PlayerBucket,
  SUPPORT_NOTE_MAX_CHARACTERS,
  type SupportAdjustmentReason,
} from "@pax/api-contract/accounting/v1alpha1";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { rawFromTypedCredits } from "@/features/credits/format";
import { Picker } from "@/features/evals/Bubbles";
import {
  areaField,
  field,
  fieldLabel,
  ghostButton,
  primaryButton,
} from "@/features/evals/components";
import { Problem } from "@/features/staff/components";
import { api } from "@/lib/api";
import { bucketLabels, supportReasonLabels } from "./format";
import { refreshAfterSupportAdjustment } from "./queries";

type Direction = "grant" | "removal";

/**
 * Grants or removes support credits with a reason and a note. The adjustment's id is chosen when the form opens
 * and sent again on every retry, so a retry after a lost response can't record it twice.
 */
export function SupportAdjustmentForm({ userId, close }: { userId: string; close: () => void }) {
  const queryClient = useQueryClient();
  const [adjustmentRequestId] = useState(() => crypto.randomUUID());
  const [direction, setDirection] = useState<Direction>("grant");
  const [bucket, setBucket] = useState<PlayerBucket>("compensation");
  const [reason, setReason] = useState<SupportAdjustmentReason | "">("");
  const [typedAmount, setTypedAmount] = useState("");
  const [note, setNote] = useState("");
  const amountRaw = rawFromTypedCredits(typedAmount);
  const trimmedNote = note.trim();
  const adjust = useMutation({
    mutationFn: () => {
      if (amountRaw === null) throw new Error("Enter an amount greater than zero.");
      if (!reason) throw new Error("Pick a reason.");
      const body = {
        userId,
        bucket,
        amountRaw: amountRaw.toString(),
        adjustmentRequestId,
        reason,
        note: trimmedNote,
      };
      return api.accounting.support.adjustments.create({ body: { ...body, direction } });
    },
    onSuccess: async () => {
      await refreshAfterSupportAdjustment(queryClient, userId);
      close();
    },
  });
  return (
    <div className="space-y-4 py-2">
      <div className="-ml-3 flex flex-wrap items-center gap-1">
        <Picker
          label="Direction"
          value={direction}
          options={[
            { value: "grant", label: "Grant" },
            { value: "removal", label: "Remove" },
          ]}
          onChange={setDirection}
        />
        <Picker
          label="Balance"
          value={bucket}
          options={(Object.keys(bucketLabels) as PlayerBucket[]).map(value => ({
            value,
            label: bucketLabels[value],
          }))}
          onChange={setBucket}
        />
        <Picker
          label="Reason"
          value={reason}
          options={(Object.keys(supportReasonLabels) as SupportAdjustmentReason[]).map(value => ({
            value,
            label: supportReasonLabels[value],
          }))}
          onChange={setReason}
        />
      </div>
      <label className="block">
        <span className={fieldLabel}>Pax credits</span>
        <input
          inputMode="decimal"
          value={typedAmount}
          onChange={event => setTypedAmount(event.target.value)}
          placeholder="1.5"
          className={`${field} mt-1.5 w-44 tabular-nums`}
        />
        {direction === "grant" && (
          <span className="mt-1 block text-xs text-muted-foreground">
            All staff together may grant up to {MAX_SUPPORT_ISSUANCE_CREDITS_PER_DAY} Pax credits
            per UTC day.
          </span>
        )}
      </label>
      <label className="block">
        <span className={fieldLabel}>Note</span>
        <textarea
          value={note}
          maxLength={SUPPORT_NOTE_MAX_CHARACTERS}
          onChange={event => setNote(event.target.value)}
          placeholder="Why, for whoever reads this later"
          className={areaField}
        />
      </label>
      <Problem error={adjust.error} />
      <div className="flex gap-2">
        <button
          type="button"
          className={primaryButton}
          disabled={amountRaw === null || !reason || trimmedNote.length === 0 || adjust.isPending}
          onClick={() => adjust.mutate()}
        >
          {adjust.isPending
            ? "Saving…"
            : direction === "grant"
              ? "Grant credits"
              : "Remove credits"}
        </button>
        <button type="button" className={ghostButton} onClick={close}>
          Cancel
        </button>
      </div>
    </div>
  );
}
